'use client'

import { Label } from '@repo/ui'
import { AlertCircle } from 'lucide-react'
import type { ReactNode } from 'react'

import { META } from '@/lib/typography'
import { cn } from '@/lib/utils'

/**
 * One question on a position: its label, its control, and what is wrong with it.
 *
 * The refusal used to be a single amber sentence at the BOTTOM of the card,
 * under every control, saying things like "Wybierz datę." while six fields sat
 * above it — so the reader had to work out which one it meant. `pickIssue` now
 * names the field, and this is where that name is spent: the offending question
 * is marked and the sentence is printed under it, where a person looks.
 *
 * The hint keeps its place when nothing is wrong. They never both show: a rule
 * about what to type and a complaint that it is missing are the same sentence
 * twice, and the complaint is the one that matters.
 */
export function OrderField({
  label,
  htmlFor,
  hint,
  problem,
  wide,
  children,
}: {
  label: ReactNode
  /** Points at the control. Omit for a group (chips, a radio row) with no single input. */
  htmlFor?: string
  /** The rule — what makes a good answer. Hidden while `problem` is showing. */
  hint?: ReactNode
  /** What is missing or wrong, from `pickIssue`. */
  problem?: string | null
  /** Span both columns of the field grid. */
  wide?: boolean
  children: ReactNode
}) {
  return (
    <div className={cn(wide && 'sm:col-span-2')}>
      <Label
        {...(htmlFor ? { htmlFor } : {})}
        className={cn(
          'mb-2 flex items-center gap-1.5 text-[0.8rem] font-medium',
          problem ? 'text-[oklch(0.85_0.15_75)]' : 'text-white/65',
        )}
      >
        {label}
        {problem ? <AlertCircle size={12} aria-hidden="true" className="shrink-0" /> : null}
      </Label>

      {children}

      {problem ? (
        <p className={cn(META, 'mt-2 font-medium text-[oklch(0.85_0.15_75)]')}>{problem}</p>
      ) : hint ? (
        <p className={cn(META, 'mt-2 leading-relaxed text-white/55')}>{hint}</p>
      ) : null}
    </div>
  )
}
